import XLSX from "xlsx-js-style";

// ---------------------------------------------------------------------------------------------------------------
// Excel 출력용 헬퍼
// 목록은 문서 1건을 한 행으로 두고 구성성분만 칸 안에서 여러 줄(child-table)로 보여주지만,
// Excel 은 성분 1개를 1행으로 펼치고 제품명·제조사 등 문서 단위 열은 세로 병합해서 목록과 같은 모양으로 만든다.
// ---------------------------------------------------------------------------------------------------------------
const getBody = (item) => {
  try {
    return item && item.post_content ? JSON.parse(item.post_content).body || {} : {};
  } catch (e) {
    return {};
  }
};

const 함량Text = (함량) => {
  const v = 함량 || {};
  if (v.범위 === "고정값") return `${v.min}%`;
  if (v.범위 === "범위") return `${v.min}% ~ ${v.max}%`;
  return "-";
};

// 문서 1건 -> 성분 수만큼의 [구성물질, CAS, 함량] 배열. 성분이 없으면 "-" 로 채운 1행.
const buildIngredientRows = (body) => {
  const 구성성분 = Array.isArray(body.구성성분) && body.구성성분.length > 0 ? body.구성성분 : [null];
  return 구성성분.map((item) => [(item && item.구성물질) || "-", (item && item.CAS) || "-", 함량Text(item && item.함량)]);
};

const EXCEL_HEADERS = ["No", "제품명", "제조사", "용도", "구성물질", "CAS No.", "함량(%)", "MSDS 등록번호", "등록사업장", "등록자"];
// 문서 단위 열(세로 병합 대상)의 인덱스. 구성물질 / CAS No. / 함량(%) (4~6) 은 성분별로 값이 다르므로 제외.
const EXCEL_DOC_COLUMNS = [0, 1, 2, 3, 7, 8, 9];

// listAllExcel 은 로드 경로에 따라 정렬이 다를 수 있어, 화면 No. 와 동일하게 가장 오래된 문서가 1번이 되도록 id 오름차순으로 맞춘다.
const sortForExcel = (listAllExcel) =>
  (Array.isArray(listAllExcel) ? listAllExcel : []).slice().sort((a, b) => Number(a.post_id || a.id || 0) - Number(b.post_id || b.id || 0));

// 문서별로 { docValues: 문서 단위 열 값, ingredients: 성분 행들 } 묶음을 만든다.
const buildExcelDocs = (listAllExcel) =>
  sortForExcel(listAllExcel).map((item, idx) => {
    const body = getBody(item);
    const stdInfo = body.stdInfo || {};
    return {
      no: idx + 1,
      제품명: body.제품명 || "",
      제조사: body.제조사 || "",
      용도: body.용도 || "",
      msds등록번호: body.msds등록번호 || "-",
      등록사업장: stdInfo.field_name || "",
      등록자: stdInfo.writer_name || "",
      ingredients: buildIngredientRows(body),
    };
  });

const toExcelRow = (doc, ingredient) => [doc.no, doc.제품명, doc.제조사, doc.용도, ingredient[0], ingredient[1], ingredient[2], doc.msds등록번호, doc.등록사업장, doc.등록자];

const list = {
  // 미리보기(팝업) 용. 성분마다 한 행으로 펼치고, 병합될 문서 단위 열은 첫 행에만 값을 넣어 Excel 결과와 같은 모양으로 보여준다.
  getExcelList(parsedListForTable, listAllExcel) {
    const rows = [];
    buildExcelDocs(listAllExcel).forEach((doc) => {
      doc.ingredients.forEach((ingredient, i) => {
        const values = toExcelRow(doc, ingredient);
        const row = {};
        EXCEL_HEADERS.forEach((h, c) => {
          row[h] = i > 0 && EXCEL_DOC_COLUMNS.includes(c) ? "" : values[c];
        });
        rows.push(row);
      });
    });
    return rows;
  },

  // 실제 다운로드. 성분 1개 = 1행, 문서 단위 열은 성분 수만큼 세로 병합한다.
  customExcelDownload(data, fileName, listAllExcel) {
    const wsData = [EXCEL_HEADERS];
    const merges = [];

    buildExcelDocs(listAllExcel).forEach((doc) => {
      const startRow = wsData.length; // 헤더가 0행이므로 데이터는 1행부터
      doc.ingredients.forEach((ingredient) => wsData.push(toExcelRow(doc, ingredient)));
      const endRow = wsData.length - 1;
      if (endRow > startRow) {
        EXCEL_DOC_COLUMNS.forEach((c) => merges.push({ s: { r: startRow, c }, e: { r: endRow, c } }));
      }
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!merges"] = merges;
    ws["!cols"] = [{ wch: 6 }, { wch: 28 }, { wch: 22 }, { wch: 24 }, { wch: 40 }, { wch: 16 }, { wch: 14 }, { wch: 22 }, { wch: 12 }, { wch: 10 }];

    // 병합된 셀은 세로 가운데 정렬이 아니면 값이 맨 위 성분 행에만 붙어 보이므로 전체 셀에 가운데 정렬 + 테두리를 준다.
    const border = { style: "thin", color: { rgb: "CCCCCC" } };
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: "s", v: "" };
        ws[addr].s = {
          alignment: { horizontal: "center", vertical: "center", wrapText: true },
          border: { top: border, bottom: border, left: border, right: border },
          ...(r === 0 ? { font: { bold: true }, fill: { fgColor: { rgb: "F2F2F2" } } } : {}),
        };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MSDS");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  },

  // =================================================================================================================
  components() {
    return [
      {
        component: "search-keyword",
        label: "제품명",
        value: "제품명",
        query: { i_name: "keyword" },
        options: {
          default: "",
        },
      },
      {
        component: "search-keyword",
        label: "제조사",
        value: "제조사",
        query: { i_maker: "keyword" },
        options: {
          default: "",
        },
      },
      {
        component: "search-keyword",
        label: "용도",
        value: "용도",
        query: { i_pp: "keyword" },
        options: {
          default: "",
        },
      },
      { component: "empty-space" },
      {
        component: "search-keyword",
        label: "구성성분",
        value: "elements",
        query: { el: "keyword" },
        options: {
          default: "",
        },
      },
      {
        component: "search-keyword",
        label: "등록사업장",
        value: "등록사업장",
        query: { r_field: "keyword" },
        options: {
          default: "",
        },
      },
      {
        component: "search-keyword",
        label: "등록자",
        value: "등록자",
        query: { r_writer: "keyword" },
        options: {
          default: "",
        },
      },
      { component: "empty-space" },
      {
        component: "search-check-group",
        label: "주요관리항목",
        value: "mn_check",
        query: { ck_l: "checkList" },
        options: {
          list: [
            { text: "특별관리물질", value: "특별관리물질" },
            { text: "관리대상물질", value: "관리대상물질" },
            { text: "작업환경측정", value: "작업환경측정" },
            { text: "특수건강검진", value: "특수건강검진" },

            { text: "유독물질", value: "유독물질" },
            { text: "제한물질", value: "제한물질" },
            { text: "금지물질", value: "금지물질" },
            { text: "사고대비물질", value: "사고대비물질" },
          ],
          selectMaxCount: 8,
          default: [],
        },
      },
    ];
  },
  getConditions: (nav) => {
    const conditions = [];
    const search = nav.value;

    if (search.제품명) {
      conditions.push({
        key: "body.제품명",
        value: `%${search.제품명}%`,
        operator: "LIKE",
      });
    }

    if (search.제조사) {
      conditions.push({
        key: "body.제조사",
        value: `%${search.제조사}%`,
        operator: "LIKE",
      });
    }

    if (search.용도) {
      conditions.push({
        key: "body.용도",
        value: `%${search.용도}%`,
        operator: "LIKE",
      });
    }

    if (search.등록사업장) {
      conditions.push({
        key: "body.stdInfo.field_name",
        value: `%${search.등록사업장}%`,
        operator: "LIKE",
      });
    }

    if (search.등록자) {
      conditions.push({
        key: "body.stdInfo.writer_name",
        value: `%${search.등록자}%`,
        operator: "LIKE",
      });
    }

    if (search.elements) {
      const subConditions = [];
      const elements = search.elements.split(",").map((e) => e.trim());
      if (elements.length > 0) {
        for (const element of elements) {
          const firstCondition = {
            key: "body.구성성분[%].구성물질",
            value: `%${element}%`,
            operator: "LIKE",
          };
          if (subConditions.length > 0) {
            firstCondition.boolean = "OR";
          }
          subConditions.push(firstCondition);
          subConditions.push({
            key: "body.구성성분[%].CAS",
            value: `%${element}%`,
            operator: "LIKE",
            boolean: "OR",
          });
        }
      }

      conditions.push({
        boolean: "AND",
        conditions: subConditions,
      });
    }

    if (search.mn_check && search.mn_check.length > 0) {
      const subConditions = [];
      for (const mnCheck of search.mn_check) {
        const sub = {
          key: `body.구성성분[%].${mnCheck}`,
          value: "1",
          operator: "IN",
        };
        if (subConditions.length > 0) {
          sub.boolean = "OR";
        }
        subConditions.push(sub);
      }

      conditions.push({
        boolean: "AND",
        conditions: subConditions,
      });
    }

    return conditions;
  },

  // =================================================================================================================
  getHead: () => {
    return [
      {
        name: "제품명",
        key: "제품명",
        pivot: true,
        /**
         * ellipsis : 기본값, 말줄임
         * wrap : 전체 표시
         * clip : 잘라내기
         */
        textDisplay: "wrap",
      },
      {
        name: "제조사",
        key: "",
        textDisplay: "wrap",
      },
      {
        name: "용도",
        key: "",
        textDisplay: "wrap",
      },
      {
        name: "구성물질 및 함유량",
        style: { width: "560px" },
        child: [{ name: "구성물질" }, { name: "CAS No." }, { name: "함량(%)" }],
      },
      {
        name: "MSDS 등록번호",
        key: "body.msds등록번호",
      },
      {
        name: "등록사업장",
        key: "body.stdInfo.field_name",
      },
      {
        name: "등록자",
        key: "body.stdInfo.writer_name",
      },
    ];
  },
  getCol: (index, value, func) => {
    try {
      switch (index) {
        case 0: // 제품명
          return func.getContent(value).제품명;
        case 1: // 제조사
          return func.getContent(value).제조사;
        case 2: // 용도
          return func.getContent(value).용도;
        case 3: {
          // 구성성분 (성분별로 구성물질명 / CAS / 함량 3칸씩 한 줄, 값이 없으면 각 칸에 "-")
          const 구성성분 = func.getContent(value).구성성분 || [];
          const buildRow = (item) => {
            const 함량 = (item && item.함량) || {};
            const name = (item && item.구성물질) || "-";
            let casText = "-";
            if (item && item.CAS) {
              casText = item.CAS;
              if (함량.범위 && 함량.범위 !== "없음") casText += " ...";
            }
            return [name, casText, 함량Text(함량)];
          };
          return {
            type: "child-table",
            rows: 구성성분.length > 0 ? 구성성분.map(buildRow) : [buildRow(null)],
          };
        }
        case 4: // MSDS 등록번호
          return func.getContent(value).msds등록번호 || "-";
        case 5: // 등록사업장
          return func.getContent(value).stdInfo.field_name;
        case 6: // 등록자
          return func.getContent(value).stdInfo.writer_name;
      }
    } catch (e) {
      console.log("e", e);
      /*ignore*/
    }
    return "";
  },

  getSearchKeyValue: (nav) => {},
  getSearchKeyword: (nav) => {},

  // "경고표지 출력" (List 상단 버튼)에서 선택된 행마다 라벨에 필요한 데이터만 뽑아서 넘긴다.
  getGhsLabelData: (value, func) => {
    const body = func.getContent(value) || {};
    const 경고표지요소 = body.경고표지요소 || {};
    return {
      postId: value.post_id || value.id,
      제품명: body.제품명 || "",
      제조사: body.제조사 || "",
      제조사전화번호: body.제조사전화번호 || "",
      그림문자: 경고표지요소.그림문자 || {},
      신호어: 경고표지요소.신호어 || "없음",
      유해위험문구: 경고표지요소.유해위험문구 || [],
      예방조치문구: 경고표지요소.예방조치문구 || [],
    };
  },
};

export default list;
